import { useEffect } from 'react';
import { type StyleProp, View, type ViewStyle } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import type { UnistylesVariants } from 'react-native-unistyles';
import { StyleSheet } from 'react-native-unistyles';

export type SpinnerProps = UnistylesVariants<typeof style> & {
  containerStyle?: StyleProp<ViewStyle>;
  spinnerStyle?: StyleProp<ViewStyle>;
};

const DEFAULT_DURATION = 1000;

export default function Spinner({
  variant,
  size,
  containerStyle,
  spinnerStyle,
}: SpinnerProps) {
  style.useVariants({
    variant,
    size,
  });

  const rotation = useSharedValue(0);

  useEffect(() => {
    rotation.value = withRepeat(
      withTiming(1, {
        duration: Math.max(DEFAULT_DURATION, 200),
        easing: Easing.linear,
      }),
      -1,
    );

    return () => {
      rotation.value = 0;
    };
  }, [rotation]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value * 360}deg` }],
  }));

  return (
    <View style={[style.container, containerStyle]}>
      <Animated.View style={[style.spinner, animatedStyle, spinnerStyle]} />
    </View>
  );
}

const style = StyleSheet.create((theme) => ({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
    borderColor: theme.colors.borderMuted,
    variants: {
      size: {
        sm: {
          width: theme.size(6),
          height: theme.size(6),
          borderWidth: theme.size(1),
          borderRadius: theme.size(3),
        },
        md: {
          width: theme.size(8),
          height: theme.size(8),
          borderWidth: theme.size(1.5),
          borderRadius: theme.size(4),
        },
        lg: {
          width: theme.size(12),
          height: theme.size(12),
          borderWidth: theme.size(2),
          borderRadius: theme.size(6),
        },
        xl: {
          width: theme.size(16),
          height: theme.size(16),
          borderWidth: theme.size(3),
          borderRadius: theme.size(8),
        },
        default: {
          width: theme.size(8),
          height: theme.size(8),
          borderWidth: theme.size(2),
          borderRadius: theme.size(4),
        },
      },
      variant: {
        primary: {
          borderColor: theme.colors.primaryMuted,
        },
        secondary: {
          borderColor: theme.colors.secondaryMuted,
        },
        success: {
          borderColor: theme.colors.successMuted,
        },
        info: {
          borderColor: theme.colors.infoMuted,
        },
        warning: {
          borderColor: theme.colors.warningMuted,
        },
        error: {
          borderColor: theme.colors.errorMuted,
        },
        ghost: {},
        default: {
          borderColor: theme.colors.surfaceMuted,
        },
      },
    },
  },
  spinner: {
    position: 'absolute',
    borderTopColor: theme.colors.surfaceInverted,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: 'transparent',
    variants: {
      size: {
        sm: {
          width: theme.size(6),
          height: theme.size(6),
          borderWidth: theme.size(1),
          borderRadius: theme.size(3),
        },
        md: {
          width: theme.size(8),
          height: theme.size(8),
          borderWidth: theme.size(1.5),
          borderRadius: theme.size(4),
        },
        lg: {
          width: theme.size(12),
          height: theme.size(12),
          borderWidth: theme.size(2),
          borderRadius: theme.size(6),
        },
        xl: {
          width: theme.size(16),
          height: theme.size(16),
          borderWidth: theme.size(3),
          borderRadius: theme.size(8),
        },
        default: {
          width: theme.size(8),
          height: theme.size(8),
          borderWidth: theme.size(2),
          borderRadius: theme.size(4),
        },
      },
      variant: {
        primary: {
          borderTopColor: theme.colors.primary,
        },
        secondary: {
          borderTopColor: theme.colors.secondary,
        },
        success: {
          borderTopColor: theme.colors.success,
        },
        info: {
          borderTopColor: theme.colors.info,
        },
        warning: {
          borderTopColor: theme.colors.warning,
        },
        error: {
          borderTopColor: theme.colors.error,
        },
        ghost: {},
        default: {
          borderTopColor: theme.colors.surfaceInverted,
        },
      },
    },
  },
}));
